import { hasPinSession } from './pin-session';
import { vaultPolicy, sealPayload, openPayload } from './oem-vault';
import { oemHandoverAcknowledgementSchema, type OemCommand } from '@sqi/shared-schemas';
import type { OemContext } from './auth';
import { offlineDatabase, type PendingMutation } from './offline-db';
import { oemCommand, recoverReceipt, OemApiError, acknowledgeHandover } from './oem-client';
export async function retainOnlineCommand(
  context: OemContext,
  userId: string,
  action: string,
  body: OemCommand,
) {
  if (hasPinSession() || !vaultPolicy()) return;
  const pending: PendingMutation = {
    id: body.idempotencyKey,
    organizationId: context.organizationId,
    userId,
    entityType: 'oem:online:' + action,
    entityId: body.target?.id ?? body.idempotencyKey,
    operation: 'create',
    baseVersion: body.target?.version ?? 0,
    payload: { projectId: context.projectId, action, body },
    createdAt: new Date().toISOString(),
    retryCount: 0,
    idempotencyKey: body.idempotencyKey,
  };
  await offlineDatabase.mutations.put({
    ...pending,
    payload: await sealPayload(userId, context.organizationId, pending.payload),
  });
  dispatchEvent(new Event('sqi-offline-queue-changed'));
}
export async function settleOnlineCommand(key: string) {
  await offlineDatabase.mutations.delete(key);
  dispatchEvent(new Event('sqi-offline-queue-changed'));
}
export async function recoverOnlineCommand(
  context: OemContext,
  userId: string,
  mutation: PendingMutation,
) {
  if (hasPinSession()) throw new OemApiError('online_required', 403);
  if (
    mutation.userId !== userId ||
    mutation.organizationId !== context.organizationId ||
    !mutation.entityType.startsWith('oem:online:')
  )
    throw new OemApiError('scope_denied', 403);
  const payload = await openPayload<{ projectId: string; action: string; body: OemCommand }>(
    userId,
    context.organizationId,
    mutation.payload,
  );
  if (payload.projectId !== context.projectId) throw new OemApiError('scope_denied', 403);
  if (!navigator.onLine) throw new OemApiError('online_required', 503);
  try {
    const receipt = await recoverReceipt(context, mutation.idempotencyKey);
    await settleOnlineCommand(mutation.id);
    return receipt;
  } catch (e) {
    if (!(e instanceof OemApiError) || e.status !== 404) throw e;
    const result =
      payload.action === 'acknowledge_handover'
        ? await acknowledgeHandover(oemHandoverAcknowledgementSchema.parse(payload.body.payload))
        : await oemCommand(payload.action, payload.body, context);
    await settleOnlineCommand(mutation.id);
    return result;
  }
}
